import { datePl, type MembershipChangeDto, shiftDate, type TeamDto, todayInWarsaw } from '@bqcrm/shared';
import { Alert, Button, Card, Group, Modal, Stack, Text, TextInput } from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../../api';
import { QueryState } from '../../components/QueryState';
import { notifyError, notifyOk } from '../../notify';
import { ConflictList } from './common';

/** Prośba o przeniesienie stałego członkostwa z ekipy `from` do `to`. */
export interface MoveRequest {
  membershipId: string;
  userId: string;
  fullName: string;
  from: TeamDto;
  to: TeamDto;
}

const isDate = (v: string) => /^\d{4}-\d{2}-\d{2}$/.test(v);
const previewMove = (req: MoveRequest, date: string) =>
  api<MembershipChangeDto>(`/team-memberships/${req.membershipId}/move/preview`, { method: 'POST', body: { teamId: req.to.id, date } });

/** Widok po przeniesieniu (zanim serwer odpowie): osoba znika ze starej ekipy i pojawia się w nowej. */
function applyMove(list: TeamDto[], req: MoveRequest, date: string): TeamDto[] {
  const old = list.find((t) => t.id === req.from.id)?.members.find((m) => m.membershipId === req.membershipId);
  if (!old) return list;
  const from = old.validFrom > date ? old.validFrom : date;
  return list.map((t) => {
    if (t.id === req.from.id) {
      return {
        ...t,
        members: old.validFrom >= date ? t.members.filter((m) => m.membershipId !== req.membershipId) : t.members.map((m) => (m.membershipId === req.membershipId ? { ...m, validTo: shiftDate(date, -1) } : m)),
      };
    }
    if (t.id === req.to.id) return { ...t, members: [...t.members, { ...old, membershipId: `tmp-${req.membershipId}`, validFrom: from }] };
    return t;
  });
}

/**
 * Przenoszenie między ekipami: najpierw podgląd skutków; bez wpływu na zaplanowane zadania — od razu zapis,
 * w przeciwnym razie okno z listą zadań do potwierdzenia. Widok zmienia się od razu i wraca przy błędzie.
 */
export function useTeamMove(onReport: (r: MembershipChangeDto) => void) {
  const qc = useQueryClient();
  const [checking, setChecking] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<{ req: MoveRequest; preview: MembershipChangeDto } | null>(null);

  const move = useMutation({
    mutationFn: (v: { req: MoveRequest; date: string; note: string }) =>
      api<MembershipChangeDto>(`/team-memberships/${v.req.membershipId}/move`, { method: 'POST', body: { teamId: v.req.to.id, date: v.date, note: v.note } }),
    onMutate: async ({ req, date }) => {
      await qc.cancelQueries({ queryKey: ['teams'] });
      const prev = qc.getQueryData<TeamDto[]>(['teams']);
      if (prev) qc.setQueryData<TeamDto[]>(['teams'], applyMove(prev, req, date));
      return { prev };
    },
    onError: (err, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(['teams'], ctx.prev);
      notifyError(err);
    },
    onSuccess: (r, { req, date }) => {
      notifyOk(`${req.fullName} przeniesiony(a) do ekipy „${req.to.name}”${date > todayInWarsaw() ? ` od ${datePl(date)}` : ''}`);
      setConfirm(null);
      if (r.affected.length) onReport(r);
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['teams'] });
      void qc.invalidateQueries({ queryKey: ['planning-board'] });
    },
  });

  const start = async (req: MoveRequest) => {
    if (checking || move.isPending) return;
    const today = todayInWarsaw();
    setChecking(req.to.id);
    try {
      const preview = await previewMove(req, today);
      if (preview.affected.length) setConfirm({ req, preview });
      else move.mutate({ req, date: today, note: '' });
    } catch (err) {
      notifyError(err);
    } finally {
      setChecking(null);
    }
  };

  const dialog = confirm && (
    <MoveDialog
      req={confirm.req}
      initial={confirm.preview}
      saving={move.isPending}
      onClose={() => setConfirm(null)}
      onConfirm={(date, note) => move.mutate({ req: confirm.req, date, note })}
    />
  );
  return { start, checking, moving: move.isPending ? move.variables?.req : undefined, dialog };
}

function MoveDialog({ req, initial, saving, onClose, onConfirm }: { req: MoveRequest; initial: MembershipChangeDto; saving: boolean; onClose: () => void; onConfirm: (date: string, note: string) => void }) {
  const today = todayInWarsaw();
  const [date, setDate] = useState(today);
  const [note, setNote] = useState('');
  const valid = isDate(date) && date >= today;
  const preview = useQuery({
    queryKey: ['team-move-preview', req.membershipId, req.to.id, date],
    queryFn: () => previewMove(req, date),
    enabled: valid && date !== today,
    staleTime: 30_000,
  });
  const data = date === today ? initial : preview.data;
  return (
    <Modal opened onClose={onClose} title={`Przenieść do ekipy „${req.to.name}”?`} size="lg">
      <Stack>
        <Text size="sm">
          <b>{req.fullName}</b> przechodzi z ekipy „{req.from.name}” do „{req.to.name}”.{data?.affected.length ? ' Zmieni to przydziały w zaplanowanych zadaniach:' : ''}
        </Text>
        <Group grow align="flex-start">
          <TextInput type="date" label="Od kiedy" min={today} value={date} onChange={(e) => setDate(e.currentTarget.value)} error={isDate(date) && date < today ? 'Najwcześniej od dziś' : undefined} />
          <TextInput label="Notatka (opcjonalnie)" placeholder="np. wzmocnienie ekipy na sezon" value={note} onChange={(e) => setNote(e.currentTarget.value)} />
        </Group>
        <QueryState isLoading={date !== today && preview.isLoading} error={date !== today ? preview.error : null}>
          {data && (data.affected.length === 0 ? (
            <Alert color="green">Od tego dnia przeniesienie nie zmienia zaplanowanych zadań.</Alert>
          ) : (
            <Stack gap="xs">
              {data.affected.map((a) => (
                <Card key={a.taskId} withBorder padding="sm">
                  <Text fw={600} size="sm">
                    {a.number} — {a.title}
                  </Text>
                  {a.removed.length > 0 && <Text size="sm">Wypada z zadania: {a.removed.join(', ')}</Text>}
                  {a.added.length > 0 && <Text size="sm">Dołącza do zadania: {a.added.join(', ')}</Text>}
                  {a.conflicts.length > 0 && (
                    <Alert color="red" mt="xs" title="Nowe konflikty">
                      <ConflictList conflicts={a.conflicts} />
                    </Alert>
                  )}
                </Card>
              ))}
            </Stack>
          ))}
        </QueryState>
        <Text size="xs" c="dimmed">
          Przeliczane są tylko dni przyszłe i przydziały „przez ekipę”. Dni minione (kto faktycznie pracował) i przydziały ręczne się nie zmieniają.
        </Text>
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Anuluj
          </Button>
          <Button onClick={() => onConfirm(date, note)} loading={saving} disabled={!valid || (date !== today && !preview.data)}>
            Przenieś
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
